'use client';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUserProfile } from './user-profile-context';
import { ExtensionsByMachine } from '../interfaces/domain/installed-extensions.modal';
import { AppBusEventType } from '../interfaces/bus-events';
import useBus from '../hooks/use-bus';
import { InstalledExtensionsService } from '../services/installed-extensions-service';
import { useOverwolfStore } from '../hooks/use-overwolf-store';
import { subscribeOverwolfStoreAppsChanged } from '../services/overwolf/overwolf-store.service';

// Failsafe so a never-completing native install (cancelled/failed — no event)
// doesn't spin the in-progress indicator forever.
const INSTALL_FAILSAFE_MS = 10 * 60 * 1000;

export interface InstalledExtensionsContextValue {
  extensionsByMachine: ExtensionsByMachine | null;
  installedAppIds: Set<string>;
  // UIDs of installed Overwolf-Electron apps (detected by the shell from the
  // registry). Kept separate from `installedAppIds` since Electron apps aren't
  // native store apps — consumers opt in for the Electron flow.
  installedElectronAppIds: Set<string>;
  // Re-check a single Electron app's install state (e.g. on app-page open) and
  // fold the result into `installedElectronAppIds`. No-op outside the client.
  checkElectronAppInstalled: (uid: string) => Promise<void>;
  // appIds for which a native install was requested but isn't yet reflected as
  // installed — drives the in-progress indicator until the app-installed event
  // lands (the native client exposes no usable install-progress percentage).
  pendingInstallAppIds: Set<string>;
  // Mark a native app as installing (called when installApp is requested).
  markInstalling: (uid: string) => void;
  // True once the installed-apps source has resolved for the current auth state
  // (the REST fetch on the web, or the shell bridge read in the client). Lets
  // consumers tell "no apps installed" apart from "not loaded yet".
  isInstalledDataReady: boolean;
}

// Mounted once near the app root, this owns the single source of truth for the
// user's installed apps. The fetch (REST when on the web, or the Overwolf shell
// bridge when embedded) happens here exactly once and is shared via context, so
// the many components that need install-state don't each hit the network/bridge.
export const InstalledExtensionsContext =
  createContext<InstalledExtensionsContextValue>({
    extensionsByMachine: null,
    installedAppIds: new Set(),
    installedElectronAppIds: new Set(),
    checkElectronAppInstalled: async () => undefined,
    pendingInstallAppIds: new Set(),
    markInstalling: () => undefined,
    isInstalledDataReady: false,
  });

// -----------------------------------------------------------------------------
export const InstalledExtensionsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { isLoggedIn } = useUserProfile();
  const {
    isAvailable: isOverwolfStoreAvailable,
    getInstalledAppIds,
    getInstalledElectronAppIds,
    isElectronAppInstalled,
  } = useOverwolfStore();

  const [extensionsByMachine, setExtensionsByMachine] =
    useState<ExtensionsByMachine>(null);
  const [localInstalledAppIds, setLocalInstalledAppIds] = useState<Set<string>>(
    new Set(),
  );
  const [installedElectronAppIds, setInstalledElectronAppIds] = useState<
    Set<string>
  >(new Set());
  const [pendingInstallAppIds, setPendingInstallAppIds] = useState<Set<string>>(
    new Set(),
  );
  const [isInstalledDataReady, setIsInstalledDataReady] =
    useState<boolean>(false);
  const isMountedRef = useRef(true);

  // When embedded in the Overwolf client, the installed apps come straight from
  // the shell (over the bridge) instead of the REST API. Native store apps and
  // Electron apps (detected from the registry) are fetched together.
  const fetchLocalInstalledApps = useCallback(async (): Promise<void> => {
    const [ids, electronIds] = await Promise.all([
      getInstalledAppIds(),
      getInstalledElectronAppIds(),
    ]);

    if (isMountedRef.current) {
      setLocalInstalledAppIds(ids);
      setInstalledElectronAppIds(electronIds);
      setIsInstalledDataReady(true);
    }
  }, [getInstalledAppIds, getInstalledElectronAppIds]);

  const fetchInstalledExtensions = useCallback(
    async (forceRefresh = false): Promise<void> => {
      // Login state not resolved yet (null until the check completes). Bailing
      // here — rather than falling into the logged-out branch below — keeps
      // isInstalledDataReady false until we truly know the state. Otherwise
      // consumers read a premature "ready" with an empty result (e.g. the My
      // Account init event captured installedAppsCount: 0 before the fetch ran).
      if (isLoggedIn === null) {
        return;
      }

      // Not gated on currentUser.token: inside the Overwolf client the profile
      // carries no REST token (there's no owUser cookie in the cross-origin
      // iframe), yet the service can still resolve one over the shell bridge.
      if (!isLoggedIn) {
        setExtensionsByMachine(null);
        // Logged-out is a resolved state (no apps to fetch), so mark ready —
        // otherwise consumers can't tell "no apps" from "still loading".
        setIsInstalledDataReady(true);
        return;
      }

      const data =
        await InstalledExtensionsService.getExtensionsByMachine(forceRefresh);

      if (isMountedRef.current) {
        setExtensionsByMachine(data);
        setIsInstalledDataReady(true);
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Bridge fetch: current machine's installed apps, only when embedded in the
  // Overwolf client. It must NOT depend on `fetchInstalledExtensions` (whose
  // identity changes with auth state), or every token update during first
  // navigation would re-fire getInstalledAppIds over the bridge.
  useEffect(() => {
    if (isOverwolfStoreAvailable) {
      void fetchLocalInstalledApps();
    }
  }, [isOverwolfStoreAvailable, fetchLocalInstalledApps]);

  // REST fetch: the account's installed apps grouped by machine. Runs in BOTH
  // modes — on the web it's the sole source; inside the Overwolf client it adds
  // the user's OTHER machines alongside the bridge's current-machine list (the
  // service resolves the token over the bridge when there's no cookie). No-op
  // until logged in.
  useEffect(() => {
    void fetchInstalledExtensions();
  }, [fetchInstalledExtensions]);

  useBus(
    AppBusEventType.App.RefreshInstalledExtensions,
    () => {
      if (isOverwolfStoreAvailable) {
        void fetchLocalInstalledApps();
      } else {
        void fetchInstalledExtensions(true);
      }
    },
    [
      isOverwolfStoreAvailable,
      fetchLocalInstalledApps,
      fetchInstalledExtensions,
    ],
  );

  // Live install/uninstall events pushed by the shell. The event payload shape
  // isn't finalized yet, so we reconcile from the source of truth (getLocalApps)
  // rather than patching a single app. Once the payload is confirmed this can
  // become a targeted update by UID.
  useEffect(() => {
    if (!isOverwolfStoreAvailable) {
      return;
    }

    return subscribeOverwolfStoreAppsChanged(() => {
      void fetchLocalInstalledApps();
    });
  }, [isOverwolfStoreAvailable, fetchLocalInstalledApps]);

  const restInstalledAppIds = useMemo<Set<string>>(() => {
    if (!extensionsByMachine) return new Set();
    const ids = new Set<string>();
    for (const extensionIds of Object.values(extensionsByMachine)) {
      for (const id of extensionIds) ids.add(id);
    }
    return ids;
  }, [extensionsByMachine]);

  const installedAppIds = isOverwolfStoreAvailable
    ? localInstalledAppIds
    : restInstalledAppIds;

  // Native install requested for a uid: show the in-progress indicator until the
  // app shows up as installed. A long failsafe clears it if the install never
  // completes (e.g. cancelled/failed — the client emits no failure event).
  const markInstalling = useCallback((uid: string): void => {
    if (!uid) {
      return;
    }
    setPendingInstallAppIds((prev) =>
      prev.has(uid) ? prev : new Set(prev).add(uid),
    );
    setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      setPendingInstallAppIds((prev) => {
        if (!prev.has(uid)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    }, INSTALL_FAILSAFE_MS);
  }, []);

  // Clear pending entries once the app is actually installed.
  useEffect(() => {
    setPendingInstallAppIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      let changed = false;
      const next = new Set(prev);
      for (const uid of prev) {
        if (installedAppIds.has(uid)) {
          next.delete(uid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [installedAppIds]);

  // Re-check a single Electron app and fold the result into the set. Used on
  // app-page open so the page reflects the current on-disk state without
  // refetching everything.
  const checkElectronAppInstalled = useCallback(
    async (uid: string): Promise<void> => {
      if (!isOverwolfStoreAvailable || !uid) {
        return;
      }
      const installed = await isElectronAppInstalled(uid);
      if (!isMountedRef.current) {
        return;
      }
      setInstalledElectronAppIds((prev) => {
        const has = prev.has(uid);
        if (installed === has) {
          return prev;
        }
        const next = new Set(prev);
        if (installed) {
          next.add(uid);
        } else {
          next.delete(uid);
        }
        return next;
      });
    },
    [isOverwolfStoreAvailable, isElectronAppInstalled],
  );

  const value = useMemo<InstalledExtensionsContextValue>(
    () => ({
      extensionsByMachine,
      installedAppIds,
      installedElectronAppIds,
      checkElectronAppInstalled,
      pendingInstallAppIds,
      markInstalling,
      isInstalledDataReady,
    }),
    [
      extensionsByMachine,
      installedAppIds,
      installedElectronAppIds,
      checkElectronAppInstalled,
      pendingInstallAppIds,
      markInstalling,
      isInstalledDataReady,
    ],
  );

  return (
    <InstalledExtensionsContext.Provider value={value}>
      {children}
    </InstalledExtensionsContext.Provider>
  );
};
